import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coveragePresetApi } from '../api/coveragePresetApi';
import type {
  CreateCoveragePresetDto,
  UpdateCoveragePresetDto,
  UpdatePresetPartsDto,
} from '../types/coverage-preset.types';
import { useNotifications } from '@/contexts/NotificationContext';

/**
 * Hooks React Query pour la gestion des presets de couverture
 */

// ==================== QUERIES (lecture) ====================

/**
 * Récupérer les presets pour un type de véhicule
 */
export const useCoveragePresets = (vehicleType: number | undefined, lang = 'fr') => {
  return useQuery({
    queryKey: ['coverage-presets', vehicleType, lang],
    queryFn: () => coveragePresetApi.getPresetsByVehicleType(vehicleType!, lang),
    enabled: vehicleType !== undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes (ces données changent rarement)
  });
};

/**
 * Récupérer un preset par ID avec tous ses détails
 */
export const useCoveragePreset = (id: number | undefined, lang = 'fr') => {
  return useQuery({
    queryKey: ['coverage-preset', id, lang],
    queryFn: () => coveragePresetApi.getPresetById(id!, lang),
    enabled: id !== undefined,
    staleTime: 5 * 60 * 1000,
  });
};

// ==================== MUTATIONS (écriture) ====================

/**
 * Créer un nouveau preset
 */
export const useCreateCoveragePreset = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: (preset: CreateCoveragePresetDto) => coveragePresetApi.createPreset(preset),
    onSuccess: (data) => {
      // Invalider le cache pour le vehicleType concerné
      queryClient.invalidateQueries({
        queryKey: ['coverage-presets', data.vehicleType],
      });
      showNotification('success', 'Preset créé avec succès');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la création du preset';
      showNotification('error', message);
    },
  });
};

/**
 * Mettre à jour un preset (informations générales)
 */
export const useUpdateCoveragePreset = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, preset }: { id: number; preset: UpdateCoveragePresetDto }) =>
      coveragePresetApi.updatePreset(id, preset),
    onSuccess: (data) => {
      // Invalider les caches concernés
      queryClient.invalidateQueries({
        queryKey: ['coverage-presets', data.vehicleType],
      });
      queryClient.invalidateQueries({
        queryKey: ['coverage-preset', data.id],
      });
      showNotification('success', 'Preset mis à jour avec succès');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || 'Erreur lors de la mise à jour du preset';
      showNotification('error', message);
    },
  });
};

/**
 * Mettre à jour les pièces d'un preset
 */
export const useUpdatePresetParts = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, partsDto }: { id: number; partsDto: UpdatePresetPartsDto }) =>
      coveragePresetApi.updatePresetParts(id, partsDto),
    onSuccess: (data) => {
      // Invalider les caches concernés
      queryClient.invalidateQueries({
        queryKey: ['coverage-presets', data.vehicleType],
      });
      queryClient.invalidateQueries({
        queryKey: ['coverage-preset', data.id],
      });
      showNotification('success', 'Pièces du preset mises à jour avec succès');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || 'Erreur lors de la mise à jour des pièces';
      showNotification('error', message);
    },
  });
};

/**
 * Supprimer un preset
 */
export const useDeleteCoveragePreset = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id }: { id: number; vehicleType: number }) =>
      coveragePresetApi.deletePreset(id),
    onSuccess: (_, variables) => {
      // Invalider le cache pour le vehicleType concerné
      queryClient.invalidateQueries({
        queryKey: ['coverage-presets', variables.vehicleType],
      });
      showNotification('success', 'Preset supprimé avec succès');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression du preset';
      showNotification('error', message);
    },
  });
};
